import { z } from 'zod';
import { CALENDARS, LOCALES, type CalendarKind, type Locale, type Role } from './enums';
import { normalizeEthiopianPhone } from './phone';
import { tenantSettingsSchema, type TenantSettings } from './settings';

export const OWNER_PASSWORD_MIN = 10;

/** PL-2: exactly 6 digits, not all the same digit, not a straight sequence (123456, 654321). */
export function pinProblem(pin: string): 'FORMAT' | 'REPEATED' | 'SEQUENCE' | null {
  if (!/^\d{6}$/.test(pin)) return 'FORMAT';
  if (/^(\d)\1{5}$/.test(pin)) return 'REPEATED';
  const digits = pin.split('').map(Number);
  let asc = true;
  let desc = true;
  for (let i = 1; i < digits.length; i++) {
    if (digits[i] !== digits[i - 1]! + 1) asc = false;
    if (digits[i] !== digits[i - 1]! - 1) desc = false;
  }
  return asc || desc ? 'SEQUENCE' : null;
}

export const pinSchema = z.string().refine((p) => pinProblem(p) === null, {
  message: 'PIN must be 6 digits, not repeated and not a sequence',
});

export const passwordSchema = z.string().min(OWNER_PASSWORD_MIN).max(200);

export const phoneInputSchema = z
  .string()
  .transform((v, ctx) => {
    const p = normalizeEthiopianPhone(v);
    if (!p) {
      ctx.addIssue({ code: 'custom', message: 'Invalid Ethiopian phone number' });
      return z.NEVER;
    }
    return p;
  });

export const loginRequestSchema = z.object({
  identifier: z.string().trim().min(1).max(200),
  secret: z.string().min(1).max(200),
  deviceId: z.string().trim().min(1).max(100),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const createManagerSchema = z.object({
  name: z.string().trim().min(1).max(60),
  phone: phoneInputSchema,
  pin: pinSchema,
});

export const resetPinSchema = z.object({ pin: pinSchema });

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

export const updateMeSchema = z
  .object({
    locale: z.enum(LOCALES).optional(),
    primaryCalendar: z.enum(CALENDARS).optional(),
  })
  .refine((v) => v.locale !== undefined || v.primaryCalendar !== undefined, 'Nothing to update');

export const updateTenantSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    settings: tenantSettingsSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.settings !== undefined, 'Nothing to update');

export interface UserDto {
  id: string;
  tenantId: string;
  name: string;
  phone: string;
  email: string | null;
  role: Role;
  locale: Locale;
  primaryCalendar: CalendarKind;
  active: boolean;
}

export interface TenantDto {
  id: string;
  name: string;
  timezone: string;
  currency: string;
  settings: TenantSettings;
  updatedAt: string;
}

export interface LoginResponse {
  accessToken: string;
  user: UserDto;
  tenant: TenantDto;
}

export type RefreshResponse = LoginResponse;
